import { partnerGroupChannelQueryKeys } from '../../../api/partner-group-channel-query-keys';
import {
  buildReactionChips,
  deriveOwnReactionsFromLatest,
  enrichLatestReactionsWithIdentityLookup,
  mergeLatestReactionIdentitiesFromPrevious,
  mergeReactionUserIdentity,
} from './communityChatReactions';

export const PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE = 20;

const mergeReactionFields = (message, reactionPatch = {}) => {
  const reactionCounts = reactionPatch.reactionCounts ?? message.reactionCounts ?? {};

  // Don't replace existing latest_reactions with [] while counts still exist
  // (thin API / optimistic responses often omit reactor rows).
  let latestReactions = message.latestReactions ?? [];
  if (Array.isArray(reactionPatch.latestReactions)) {
    if (reactionPatch.latestReactions.length > 0) {
      latestReactions = mergeLatestReactionIdentitiesFromPrevious(
        reactionPatch.latestReactions,
        message.latestReactions ?? [],
      );
    } else {
      const hasCounts = Object.values(reactionCounts).some((count) => Number(count) > 0);
      latestReactions = hasCounts ? (message.latestReactions ?? []) : [];
    }
  }

  const ownReactions =
    reactionPatch.ownReactions !== undefined
      ? reactionPatch.ownReactions
      : (message.ownReactions ?? []);
  const ownReaction =
    reactionPatch.ownReaction !== undefined
      ? reactionPatch.ownReaction
      : (message.ownReaction ?? null);

  return {
    reactionCounts,
    ownReactions,
    ownReaction,
    latestReactions,
    reactionChips:
      reactionPatch.reactionChips ??
      buildReactionChips({ reactionCounts, ownReaction, ownReactions, latestReactions }),
  };
};

const sameMessageId = (left, right) => {
  const a = String(left ?? '').trim();
  const b = String(right ?? '').trim();
  return Boolean(a) && a === b;
};

const messageExistsInPages = (pages, messageId) =>
  pages.some((page) =>
    (page?.messages ?? []).some((message) => sameMessageId(message?.id, messageId)),
  );

const mapPagesWithMessageUpdate = (pages, messageId, updater) => {
  let updated = false;

  const nextPages = pages.map((page) => {
    const messages = page?.messages ?? [];
    if (!messages.some((message) => sameMessageId(message?.id, messageId))) {
      return page;
    }

    updated = true;
    return {
      ...page,
      messages: messages.map((message) =>
        sameMessageId(message?.id, messageId) ? updater(message) : message,
      ),
    };
  });

  return updated ? nextPages : null;
};

/** Update reaction fields on a cached message across all message query pages. */
export const updatePartnerGroupChatMessageReactionsInCache = (
  queryClient,
  messageId,
  reactionPatch,
) => {
  if (!messageId || !reactionPatch) {
    return false;
  }

  let updated = false;

  queryClient.setQueriesData(
    { queryKey: [...partnerGroupChannelQueryKeys.all, 'messages'] },
    (current) => {
      if (!current?.pages?.length) {
        return current;
      }

      const nextPages = mapPagesWithMessageUpdate(current.pages, messageId, (message) => ({
        ...message,
        ...mergeReactionFields(message, reactionPatch),
      }));

      if (!nextPages) {
        return current;
      }

      updated = true;
      return {
        ...current,
        pages: nextPages,
      };
    },
  );

  return updated;
};

/** Apply optimistic reaction toggle to cached message. */
export const applyPartnerGroupChatOptimisticReactionInCache = (
  queryClient,
  messageId,
  reactionType,
  applyOptimisticReaction,
) => {
  if (!messageId || !reactionType || typeof applyOptimisticReaction !== 'function') {
    return false;
  }

  let updated = false;

  queryClient.setQueriesData(
    { queryKey: [...partnerGroupChannelQueryKeys.all, 'messages'] },
    (current) => {
      if (!current?.pages?.length) {
        return current;
      }

      const nextPages = mapPagesWithMessageUpdate(current.pages, messageId, (message) =>
        applyOptimisticReaction(message, reactionType),
      );

      if (!nextPages) {
        return current;
      }

      updated = true;
      return {
        ...current,
        pages: nextPages,
      };
    },
  );

  return updated;
};

/** Update fields on a cached message across all message query pages. */
export const updatePartnerGroupChatMessageInCache = (queryClient, messageId, updater) => {
  if (!messageId || typeof updater !== 'function') {
    return false;
  }

  let updated = false;

  queryClient.setQueriesData(
    { queryKey: [...partnerGroupChannelQueryKeys.all, 'messages'] },
    (current) => {
      if (!current?.pages?.length) {
        return current;
      }

      const nextPages = mapPagesWithMessageUpdate(current.pages, messageId, updater);

      if (!nextPages) {
        return current;
      }

      updated = true;
      return {
        ...current,
        pages: nextPages,
      };
    },
  );

  return updated;
};

const isCachedStaffIdentity = (message) => {
  if (!message || typeof message !== 'object') {
    return false;
  }

  const source = String(message.source ?? '').toLowerCase();
  if (source === 'aria' || source === 'bot') {
    return false;
  }

  const senderUid = String(message.senderUid ?? '').toLowerCase();
  if (senderUid === 'aria-bot') {
    return false;
  }

  return message.isPartnerSender === true || source === 'partner' || source === 'admin';
};

const hasUsableCachedIdentity = (message) => {
  if (!message || typeof message !== 'object') {
    return false;
  }

  const author = String(message.author ?? '').trim();
  const hasAuthor = Boolean(author) && author !== 'Member';
  const hasAvatar = Boolean(String(message.avatarUrl ?? '').trim());
  return hasAuthor || hasAvatar;
};

const sameSender = (message, { senderUid, senderId }) => {
  if (!message) return false;
  if (senderUid && message.senderUid && String(message.senderUid) === String(senderUid)) {
    return true;
  }
  if (senderId && message.senderId && String(message.senderId) === String(senderId)) {
    return true;
  }
  return false;
};

/** Reuse sender identity already in this channel when live events omit avatar/name. */
export const findSenderIdentityInChannelCache = (
  queryClient,
  { channelId, senderUid, senderId, limit = PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE } = {},
) => {
  if (!queryClient || (!senderUid && !senderId)) {
    return null;
  }

  const normalizedChannelId = channelId ? String(channelId).trim() : null;
  const current = queryClient.getQueryData(
    partnerGroupChannelQueryKeys.messages(limit, normalizedChannelId),
  );

  for (const page of current?.pages ?? []) {
    for (const message of page?.messages ?? []) {
      if (sameSender(message, { senderUid, senderId }) && hasUsableCachedIdentity(message)) {
        return message;
      }
    }
  }

  return null;
};

const collectMatchIdSet = (matchIds = []) => {
  const ids = new Set();
  (Array.isArray(matchIds) ? matchIds : [matchIds]).forEach((value) => {
    const normalized = String(value ?? '').trim();
    if (normalized) {
      ids.add(normalized);
    }
  });
  return ids;
};

const findMemberIdentityInDetailCache = (queryClient, idSet) => {
  if (!queryClient || !(idSet instanceof Set) || idSet.size === 0) {
    return null;
  }

  const detailEntries = queryClient.getQueriesData({
    queryKey: [...partnerGroupChannelQueryKeys.all, 'detail'],
  });

  for (const [, data] of detailEntries) {
    const memberPages = Array.isArray(data?.pages)
      ? data.pages.flatMap((page) => page?.channel?.members ?? [])
      : (data?.channel?.members ?? []);

    for (const member of memberPages) {
      if (!member || typeof member !== 'object') {
        continue;
      }
      const memberIds = [member.id, member.uid, member.user_id, member.userId, member.firebaseUid]
        .map((id) => String(id ?? '').trim())
        .filter(Boolean);
      if (!memberIds.some((id) => idSet.has(id))) {
        continue;
      }
      const fullName = String(member.fullName ?? member.name ?? '').trim();
      const avatarUrl = String(member.avatarUrl ?? member.image ?? '').trim();
      if (!fullName && !avatarUrl) {
        continue;
      }
      return {
        fullName: fullName || undefined,
        avatarUrl: avatarUrl || undefined,
      };
    }
  }

  return null;
};

/**
 * Resolve reactor name/avatar from channel messages or members when socket Stream
 * latest_reactions only include Stream user stubs (name, no DB avatarUrl).
 */
export const findReactorIdentityInChannelCache = (
  queryClient,
  { matchIds = [], channelId, limit = PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE } = {},
) => {
  const idSet = collectMatchIdSet(matchIds);
  if (!queryClient || idSet.size === 0) {
    return null;
  }

  const normalizedChannelId = channelId ? String(channelId).trim() : null;
  const current = queryClient.getQueryData(
    partnerGroupChannelQueryKeys.messages(limit, normalizedChannelId),
  );

  for (const page of current?.pages ?? []) {
    for (const message of page?.messages ?? []) {
      const senderUid = message?.senderUid ? String(message.senderUid).trim() : '';
      const senderId = message?.senderId ? String(message.senderId).trim() : '';
      if (
        ((senderUid && idSet.has(senderUid)) || (senderId && idSet.has(senderId))) &&
        hasUsableCachedIdentity(message)
      ) {
        return {
          fullName: message.author,
          avatarUrl: message.avatarUrl,
        };
      }
    }
  }

  return findMemberIdentityInDetailCache(queryClient, idSet);
};

/**
 * Enrich socket-mapped latest_reactions with avatars from messages / members / partner defaults.
 */
export const enrichLatestReactionsFromChannelCache = (
  queryClient,
  latestReactions,
  {
    channelId,
    limit = PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE,
    senderDefaults = null,
    currentUserIds = null,
  } = {},
) =>
  enrichLatestReactionsWithIdentityLookup(latestReactions, (user) => {
    const matchIds = [...(user?.matchIds ?? []), user?.id, user?.uid].filter(Boolean);
    const fromCache = findReactorIdentityInChannelCache(queryClient, {
      matchIds,
      channelId,
      limit,
    });

    const isCurrentUser =
      currentUserIds instanceof Set && matchIds.some((id) => currentUserIds.has(String(id)));
    if (!isCurrentUser || !(senderDefaults?.avatarUrl || senderDefaults?.fullName)) {
      return fromCache;
    }

    return mergeReactionUserIdentity(
      {
        fullName: senderDefaults.fullName,
        avatarUrl: senderDefaults.avatarUrl,
      },
      fromCache,
    );
  });

/**
 * Finalize a mapped socket reaction update for cache write:
 * enrich avatars, derive this viewer's own reactions (never trust socket own_reactions).
 */
export const buildPartnerGroupChatSocketReactionCachePatch = (
  queryClient,
  reactionUpdate,
  { channelId, messageMapOptions } = {},
) => {
  if (!reactionUpdate?.messageId) {
    return null;
  }

  const latestReactions = enrichLatestReactionsFromChannelCache(
    queryClient,
    reactionUpdate.latestReactions,
    {
      channelId,
      senderDefaults: messageMapOptions?.senderDefaults,
      currentUserIds: messageMapOptions?.currentUserIds,
    },
  );

  const ownReactions = deriveOwnReactionsFromLatest(latestReactions, {
    currentUserIds: messageMapOptions?.currentUserIds,
    partnerDisplayName: messageMapOptions?.partnerDisplayName,
  });
  const ownReaction = ownReactions[0] ?? null;
  const reactionCounts = reactionUpdate.reactionCounts ?? {};

  return {
    reactionCounts,
    latestReactions,
    ownReactions,
    ownReaction,
    reactionChips: buildReactionChips({
      reactionCounts,
      ownReaction,
      ownReactions,
      latestReactions,
    }),
  };
};

/** Build a reactor user for thin reaction sockets (no Stream latest_reactions). */
export const resolveReactorUserFromChannelCache = (
  queryClient,
  {
    actorIds = [],
    channelId,
    limit = PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE,
    isOwn = false,
    senderDefaults = null,
  } = {},
) => {
  const fromCache = findReactorIdentityInChannelCache(queryClient, {
    matchIds: actorIds,
    channelId,
    limit,
  });

  return mergeReactionUserIdentity(
    {
      id: actorIds[0] ?? null,
      uid: actorIds[0] ?? null,
      matchIds: actorIds,
      fullName: isOwn ? String(senderDefaults?.fullName ?? '').trim() || 'You' : 'Member',
      avatarUrl: isOwn ? senderDefaults?.avatarUrl : undefined,
    },
    fromCache,
  );
};

/**
 * Fill missing author/avatar from an earlier message by the same sender in this channel.
 * Socket payloads often omit avatarUrl even when the listing API includes it.
 */
export const applySenderIdentityFromChannelCache = (
  queryClient,
  message,
  { channelId, limit = PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE } = {},
) => {
  if (!message) {
    return message;
  }

  const hasAuthor = Boolean(String(message.author ?? '').trim()) && message.author !== 'Member';
  const hasAvatar = Boolean(String(message.avatarUrl ?? '').trim());
  if (hasAuthor && hasAvatar) {
    return message;
  }

  const prior = findSenderIdentityInChannelCache(queryClient, {
    channelId,
    senderUid: message.senderUid,
    senderId: message.senderId,
    limit,
  });

  if (!prior) {
    return message;
  }

  const priorIsStaff = isCachedStaffIdentity(prior);

  return {
    ...message,
    author: hasAuthor ? message.author : prior.author || message.author,
    avatarUrl: hasAvatar ? message.avatarUrl : prior.avatarUrl || message.avatarUrl,
    source: message.source || (priorIsStaff ? prior.source : message.source),
    isPartnerSender: message.isPartnerSender === true || priorIsStaff,
  };
};

/** Prepend a newest message onto the first (newest) infinite-query page. */
export const appendPartnerGroupChatMessageToCache = (
  queryClient,
  message,
  { limit = PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE, channelId = null } = {},
) => {
  if (!message?.id) {
    return false;
  }

  const normalizedChannelId = channelId ? String(channelId).trim() : null;
  const queryKey = partnerGroupChannelQueryKeys.messages(limit, normalizedChannelId);
  let appended = false;

  queryClient.setQueryData(queryKey, (current) => {
    if (!current?.pages?.length) {
      return current;
    }

    if (messageExistsInPages(current.pages, message.id)) {
      return current;
    }

    const pages = [...current.pages];
    const firstPage = pages[0] ?? { messages: [], status: 'found' };
    pages[0] = {
      ...firstPage,
      messages: [message, ...(firstPage.messages ?? [])],
    };

    appended = true;
    return {
      ...current,
      pages,
    };
  });

  return appended;
};
